import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Deployment, DeploymentDetail } from '../../types/api';
import Logger from '../../utils/logger';
import { useTranslation } from 'react-i18next';

interface PodStatus {
  name?: string;
  status: string;
}

interface ExtendedDeploymentDetails extends DeploymentDetail {
  pods_status?: PodStatus[];
}

interface ApplicationStatusProps {
  deployment: Deployment;
  environmentId: string;
}

const ApplicationStatus: React.FC<ApplicationStatusProps> = ({ deployment, environmentId }) => {
  const { t } = (useTranslation as any)('status');
  const [deploymentDetails, setDeploymentDetails] = useState<ExtendedDeploymentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const details = await apiService.getDeploymentDetails(deployment.id);
      setDeploymentDetails(details);
    } catch (error) {
      Logger.error('Erreur chargement status deployment:', error);
      setDeploymentDetails(null);
    } finally {
      setLoading(false);
    }
  }, [deployment.id]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const refreshStatus = async () => {
    try {
      setRefreshing(true);
      const details = await apiService.getDeploymentDetails(deployment.id);
      setDeploymentDetails(details);
    } catch (error) {
      Logger.error('Erreur rafraîchissement status deployment:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getEffectiveStatus = () => {
    if (!deploymentDetails) return deployment.statut_deploiement;

    if (deploymentDetails.is_managed_service && deploymentDetails.argocd?.health) {
      const argoHealth = deploymentDetails.argocd.health;
      switch (argoHealth) {
        case 'Healthy':
          return 'HEALTHY';
        case 'Progressing':
          return 'PENDING';
        case 'Degraded':
        case 'Missing':
          return 'FAILED';
        default:
          return deploymentDetails.status || deployment.statut_deploiement;
      }
    }

    return deploymentDetails.status || deployment.statut_deploiement;
  };

  const getStatusIcon = () => {
    if (loading || refreshing) {
      return <ArrowPathIcon className={`h-5 w-5 text-text-quaternary ${refreshing ? 'animate-spin' : ''}`} />;
    }

    if (!deploymentDetails) {
      return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
    }

    const deploymentStatus = getEffectiveStatus();

    switch (deploymentStatus) {
      case 'RUNNING':
      case 'DEPLOYED':
      case 'HEALTHY':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'FAILED':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'PENDING':
      case 'BUILDING':
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusText = () => {
    if (loading) return t('appStatus.loading') as string;
    if (!deploymentDetails) return t('appStatus.unavailable') as string;

    const deploymentStatus = getEffectiveStatus();

    const syncInfo = deploymentDetails.is_managed_service && deploymentDetails.argocd?.sync
      ? ` (${deploymentDetails.argocd.sync})`
      : '';

    switch (deploymentStatus) {
      case 'RUNNING':
        return (t('appStatus.running') as string) + syncInfo;
      case 'DEPLOYED':
        return (t('appStatus.deployed') as string) + syncInfo;
      case 'HEALTHY':
        return (t('appStatus.healthy') as string) + syncInfo;
      case 'FAILED':
        return (t('appStatus.failed') as string) + syncInfo;
      case 'PENDING':
        return (t('appStatus.pending') as string) + syncInfo;
      case 'BUILDING':
        return (t('appStatus.building') as string) + syncInfo;
      default:
        return deploymentStatus + syncInfo;
    }
  };

  const getStatusColor = () => {
    if (loading || !deploymentDetails) return 'gray';

    const deploymentStatus = getEffectiveStatus();

    switch (deploymentStatus) {
      case 'RUNNING':
      case 'DEPLOYED':
      case 'HEALTHY':
        return 'green';
      case 'FAILED':
        return 'red';
      case 'PENDING':
      case 'BUILDING':
      default:
        return 'yellow';
    }
  };

  const statusColor = getStatusColor();

  return (
    <div className={`bg-surface-2 border-l-4 border-${statusColor}-400 rounded-card shadow-card p-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <CubeIcon className="h-6 w-6 text-text-quaternary mr-3" />
          <div>
            <h3 className="text-base font-medium text-text-primary">{deployment.nom}</h3>
            <div className="flex items-center mt-1">
              {getStatusIcon()}
              <span className={`ml-2 text-sm font-medium text-${statusColor}-400`}>
                {getStatusText()}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={refreshStatus}
          disabled={refreshing}
          className="p-2 text-text-quaternary hover:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-500 rounded-md disabled:opacity-50"
          title={t('appStatus.refreshStatus') as string}
        >
          <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-text-tertiary">{t('appStatus.type')}</span>
          <span className="ml-2 font-medium text-text-primary">{deployment.type_application}</span>
        </div>
        <div>
          <span className="text-text-tertiary">{t('appStatus.replicas')}</span>
          <span className="ml-2 font-medium text-text-primary">{deployment.replicas}</span>
        </div>
        <div>
          <span className="text-text-tertiary">{t('appStatus.cpu')}</span>
          <span className="ml-2 font-medium text-text-primary">{deployment.cpu_requis} cores</span>
        </div>
        <div>
          <span className="text-text-tertiary">{t('appStatus.ram')}</span>
          <span className="ml-2 font-medium text-text-primary">{deployment.ram_requise} GB</span>
        </div>
      </div>

      {deployment.nom_de_domaine && (
        <div className="mt-3 p-3 bg-surface-3 rounded-md">
          <span className="text-text-tertiary text-sm">{t('appStatus.domain')}</span>
          <a
            href={`https://${deployment.nom_de_domaine}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 text-accent-400 hover:text-accent-300 text-sm font-medium"
          >
            {deployment.nom_de_domaine}
          </a>
        </div>
      )}

      {deployment.application_url && !deploymentDetails?.is_managed_service && (
        <div className="mt-3 p-3 bg-accent-500/12 rounded-md">
          <span className="text-text-tertiary text-sm">{t('appStatus.appUrl')}</span>
          <a
            href={deployment.application_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 text-accent-400 hover:text-accent-300 text-sm font-medium inline-flex items-center"
          >
            {deployment.application_url.replace(/^https?:\/\//, '')}
            <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
            </svg>
          </a>
        </div>
      )}

      {!deploymentDetails?.is_managed_service && deploymentDetails?.internal_host && (
        <div className="mt-3 p-3 bg-surface-3 rounded-md">
          <span className="text-text-secondary text-sm font-medium">{t('appStatus.internalService')}</span>
          <div
            className="mt-2 font-mono text-sm text-text-secondary bg-surface-4 px-2 py-1 rounded cursor-pointer hover:bg-surface-3 transition-colors flex items-center justify-between"
            onClick={() => {
              navigator.clipboard.writeText(deploymentDetails.internal_host || '');
            }}
            title={t('appStatus.clickToCopy') as string}
          >
            <span>{deploymentDetails.internal_host}</span>
            <svg className="w-4 h-4 text-text-quaternary ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="mt-2 text-xs text-text-tertiary">
            {t('appStatus.useHostname')}
          </p>
        </div>
      )}

      {deploymentDetails?.is_managed_service && deploymentDetails?.internal_host && (
        <div className="mt-3 p-3 bg-surface-3 rounded-md">
          <span className="text-text-secondary text-sm font-medium">{t('appStatus.internalHost')}</span>
          <div
            className="mt-2 font-mono text-sm text-text-secondary bg-surface-4 px-2 py-1 rounded cursor-pointer hover:bg-surface-3 transition-colors flex items-center justify-between"
            onClick={() => {
              navigator.clipboard.writeText(deploymentDetails.internal_host || '');
            }}
            title="Cliquez pour copier"
          >
            <span>{deploymentDetails.internal_host}</span>
            <svg className="w-4 h-4 text-text-quaternary ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="mt-3 text-xs text-text-tertiary">
            <p className="font-medium mb-1">{t('appStatus.connectInfo')}</p>
            <p>{t('appStatus.useHostEnvVars')}</p>
            {deploymentDetails.type_application === 'postgresql' && (
              <code className="block mt-2 p-2 bg-surface-4 rounded text-xs break-all">
                DATABASE_URL=postgresql://USER:PASSWORD@{deploymentDetails.internal_host}:5432/DATABASE
              </code>
            )}
            {deploymentDetails.type_application === 'mysql' && (
              <code className="block mt-2 p-2 bg-surface-4 rounded text-xs break-all">
                DATABASE_URL=mysql://USER:PASSWORD@{deploymentDetails.internal_host}:3306/DATABASE
              </code>
            )}
            {deploymentDetails.type_application === 'mongodb' && (
              <code className="block mt-2 p-2 bg-surface-4 rounded text-xs break-all">
                MONGODB_URI=mongodb://USER:PASSWORD@{deploymentDetails.internal_host}:27017/DATABASE
              </code>
            )}
            {deploymentDetails.type_application === 'redis' && (
              <code className="block mt-2 p-2 bg-surface-4 rounded text-xs break-all">
                REDIS_URL=redis://{deploymentDetails.internal_host}:6379
              </code>
            )}
            {deploymentDetails.type_application === 'rabbitmq' && (
              <code className="block mt-2 p-2 bg-surface-4 rounded text-xs break-all">
                RABBITMQ_URL=amqp://USER:PASSWORD@{deploymentDetails.internal_host}:5672/
              </code>
            )}
          </div>
        </div>
      )}

      {deploymentDetails && deploymentDetails.pods_status && deploymentDetails.pods_status.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-text-secondary mb-2">{t('appStatus.instanceStatus')}</h4>
          <div className="space-y-1">
            {deploymentDetails.pods_status.map((pod: PodStatus, index: number) => (
              <div key={index} className="flex items-center text-xs">
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  pod.status === 'Running' ? 'bg-green-400' :
                  pod.status === 'Failed' ? 'bg-red-400' : 'bg-yellow-400'
                }`}></div>
                <span className="text-text-secondary">{pod.name || `Instance ${index + 1}`}: {pod.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationStatus;
